import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { authAPI } from '../../config/api';

export default function VerifyEmail() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: 'loading', message: 'جاري التحقق من بريدك الإلكتروني... اذا استمر التحميل مده طويله فقط ارجع حاول تسجيل الدخول واترك الصفحه ' });
  // Guard to ensure we only attempt verification once (prevents StrictMode double-effect)
  const hasRunRef = useRef(false);

  useEffect(() => {
    if (hasRunRef.current) return; // prevent duplicate requests
    hasRunRef.current = true;

    let mounted = true;
    async function run() {
      try {
        const response = await authAPI.verifyEmail(token);
        if (!mounted) return;
        
        const successMessage = response?.data?.message || 'تم تأكيد بريدك الإلكتروني بنجاح!';
        setState({ status: 'success', message: successMessage });
        setTimeout(() => navigate('/login'), 3000);
      } catch (e) {
        if (!mounted) return;
        
        const errorMessage = e?.response?.data?.message || e?.response?.data?.error || '';
        
        // Treat already-verified as success to reflect actual DB state
        if (errorMessage.includes('مُحقق بالفعل') || 
            errorMessage.includes('already verified') ||
            errorMessage.includes('البريد الإلكتروني مُحقق')) {
          setState({ 
            status: 'success', 
            message: 'تم تأكيد بريدك الإلكتروني بنجاح!' 
          });
          setTimeout(() => navigate('/login'), 3000);
        } 
        else if (errorMessage.includes('منتهي الصلاحية') || 
                 errorMessage.includes('غير صالح') ||
                 errorMessage.includes('expired') ||
                 errorMessage.includes('invalid')) {
          setState({ 
            status: 'expired', 
            message: 'انتهت صلاحية رابط التحقق. يمكنك طلب رابط جديد من صفحة تسجيل الدخول.' 
          });
        }
        else {
          setState({ 
            status: 'error', 
            message: errorMessage || 'حدث خطأ غير متوقع. يرجى المحاولة مرة أخرى.' 
          });
        }
      }
    }
    
    if (token) {
      run();
    } else {
      setState({ status: 'error', message: 'رابط التحقق غير صالح' });
    }
    
    return () => { mounted = false; };
  }, [token]);

  const isLoading = state.status === 'loading';
  const isSuccess = state.status === 'success';
  const isAlreadyVerified = state.status === 'already-verified';
  const isExpired = state.status === 'expired';
  const isError = state.status === 'error';

  // Determine colors and icons based on status
  const getStatusConfig = () => {
    if (isLoading) return { bg: 'bg-blue-50', text: 'text-blue-600', icon: '⏳' };
    if (isSuccess) return { bg: 'bg-green-50', text: 'text-green-600', icon: '✅' };
    if (isAlreadyVerified) return { bg: 'bg-blue-50', text: 'text-blue-600', icon: 'ℹ️' };
    if (isExpired) return { bg: 'bg-yellow-50', text: 'text-yellow-600', icon: '⏰' };
    return { bg: 'bg-red-50', text: 'text-red-600', icon: '⚠️' };
  };

  const statusConfig = getStatusConfig();

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4">
      <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-8 text-center">
        <div className={`mx-auto mb-6 h-16 w-16 rounded-full flex items-center justify-center ${statusConfig.bg}`}>
          <span className={`text-3xl ${statusConfig.text}`}>
            {statusConfig.icon}
          </span>
        </div>
        
        <h1 className="text-2xl font-bold mb-4 text-gray-900">
          {isLoading && 'جاري التحقق...'}
          {isSuccess && 'تم التأكيد بنجاح!'}
          {isAlreadyVerified && 'البريد مُحقق مسبقاً'}
          {isExpired && 'انتهت صلاحية الرابط'}
          {isError && 'فشل في التحقق'}
        </h1>
        
        <p className={`mb-6 ${
          isSuccess ? 'text-green-700' : 
          isLoading ? 'text-blue-700' : 
          isAlreadyVerified ? 'text-blue-700' :
          isExpired ? 'text-yellow-700' :
          'text-red-700'
        }`}>
          {state.message}
        </p>

        {(isSuccess || isAlreadyVerified) && (
          <p className="text-sm text-gray-500 mb-4">
            سيتم توجيهك إلى صفحة تسجيل الدخول خلال 3 ثوانٍ...
          </p>
        )}

        {(isError || isExpired) && (
          <div className="space-y-3">
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
            >
              {isExpired ? 'طلب رابط جديد' : 'الرجوع لتسجيل الدخول'}
            </button>
            <button
              onClick={() => navigate('/')}
              className="w-full text-gray-600 py-2 px-4 rounded-lg hover:bg-gray-100 transition-colors"
            >
              الرجوع للصفحة الرئيسية
            </button>
          </div>
        )}

        {isLoading && (
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        )}
      </div>
    </div>
  );
}



